import { useEditorContext } from 'client/infra/renderers/editorState';
import { RuntimeWidget } from 'client/types';
import { useCartItemsCount } from 'client/widget-components/ecom/useCartItemsCount';
import { ResponsiveStylesProp } from 'client/widget-components/responsive-styles/responsiveStylesService';
import { toCSSObject } from 'client/widget-components/responsive-styles/toCSSObject';
import { normalizeStyles } from 'client/widgets/common/styles/migration/stylesMigration';
import {
    AnyStyleStructure,
    NewBPStyles,
} from 'client/widgets/common/styles/migration/types';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import withErrorBoundary from '../../widget-components/components/ErrorBoundary/withErrorBoundary';
import SvgIcon from './SvgIcon';

export interface CartProps {
    svgMarkup?: string;
    /**
     * Any of the three shapes a stored cart can be in — flat, the deprecated `rules`/`default` map
     * the editor writes, or the runtime breakpoint map Page Elements API v2 writes. normalizeStyles
     * folds all three into the current structure.
     */
    _styles?: AnyStyleStructure;
}

const RuntimeCart: RuntimeWidget<CartProps> = ({ svgMarkup, _styles }) => {
    const { isInEditor } = useEditorContext();
    const itemsCount = useCartItemsCount();

    const onCartClick = () => {
        !isInEditor && window._ecomm?.openCart();
    };

    const styles = useMemo(
        () =>
            (normalizeStyles({
                stylesInput: _styles,
                monitorKey: 'cart',
            }) ?? {}) as NewBPStyles,
        [_styles]
    );

    return (
        <CartWrapper data-auto='ssr-cart-widget'>
            <SvgIcon
                className='cart-icon'
                dataAuto='cart-icon'
                svgMarkup={svgMarkup}
                styles={styles.cartIcon}
                onClick={onCartClick}
            />
            <StyledLabel
                styles={styles.itemsCountLabel}
                className='cart-count-label'
                data-auto='cart-count-label'
                onClick={onCartClick}
            >
                {isInEditor ? itemsCount : itemsCount > 0 && itemsCount}
            </StyledLabel>
        </CartWrapper>
    );
};

const CartWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    .cart-icon,
    .cart-count-label {
        cursor: pointer;
        user-select: none;
    }

    label.cart-count-label {
        margin-inline: 5px;
    }
`;

interface StyledLabelProps {
    styles?: ResponsiveStylesProp;
}

const StyledLabel = styled.label<StyledLabelProps>`
    ${({ styles }: StyledLabelProps) => toCSSObject(styles || [])}
`;

export default withErrorBoundary({
    Comp: RuntimeCart,
    logProps: true,
    componentName: 'Cart',
    additionalInfoToLog: {
        tag: 'native-ecom',
    },
});
